/* eslint-disable lingui/no-unlocalized-strings -- U02: legacy technical status values, never UI labels. */
import type { BankSlip } from './bank-slip.wire';
import { civilDateOf } from '@/shared/format/date';

export type BankSlipAction =
  | 'download'
  | 'protest'
  | 'cancelProtest'
  | 'cancel'
  | 'updateDueDate'
  | 'updateOtherInformation'
  | 'updateAmount'
  | 'transfer'
  | 'settlement'
  | 'insertDeduction'
  | 'cancelDeduction'
  | 'registerAgain';
export function bankSlipActionStates(
  bankSlip: BankSlip,
  billingStatus: string,
  isRoot: boolean,
  now: number,
) {
  const registration = bankSlip.statusRegisterValue.toLowerCase();
  const settlement = bankSlip.statusSettlementValue.toLowerCase();
  const protest = bankSlip.statusDebtCollectionValue.toLowerCase();
  const active = registration !== 'baixado' && settlement !== 'settlement';
  const registered = registration === 'registradocip' && settlement !== 'settlement';
  const dueDate = civilDateOf(bankSlip.dueDate);
  const deadline = dueDate ? new Date(`${dueDate}T00:00:00`) : null;
  deadline?.setDate(deadline.getDate() + 2);
  const visibility: Record<BankSlipAction, boolean> = {
    download: active,
    protest: active || protest === 'devolvido',
    cancelProtest:
      ['indebtcollection', 'todebtcollection', 'returnedfromdebtcollection'].includes(
        billingStatus.toLowerCase(),
      ) && protest !== 'retirado',
    cancel: registered,
    updateDueDate: registered,
    updateOtherInformation: active || protest === 'devolvido',
    updateAmount: registration === 'registradocip',
    transfer: active,
    settlement: isRoot,
    insertDeduction: registered,
    cancelDeduction: registered,
    registerAgain:
      (registration === 'baixado' &&
        settlement === 'nosettlement' &&
        ['sustado', 'naoprotestado', 'devolvido', 'cancelado', 'erroapresentartitulo'].includes(
          protest,
        )) ||
      bankSlip.isTransferred === false,
  };
  return (Object.keys(visibility) as BankSlipAction[]).map((id) => ({
    id,
    visible: visibility[id],
    disabled:
      id === 'cancelProtest' &&
      (deadline === null || now < deadline.getTime()) &&
      billingStatus.toLowerCase() !== 'returnedfromdebtcollection',
  }));
}

export function installmentBankSlipActions(row: {
  readonly statusValue: string;
  readonly registered?: boolean;
  readonly bankSlip?: Pick<
    BankSlip,
    'barCode' | 'statusRegisterValue' | 'statusSettlementValue' | 'statusDebtCollectionValue'
  > | null;
}) {
  const registration = row.bankSlip?.statusRegisterValue.toLowerCase() ?? '';
  const settlement = row.bankSlip?.statusSettlementValue.toLowerCase() ?? '';
  const protest = row.bankSlip?.statusDebtCollectionValue.toLowerCase() ?? '';
  const exists = row.registered === true && !!row.bankSlip?.barCode;
  const registered = registration === 'registradocip' && settlement !== 'settlement';
  const protestInactive = [
    '',
    'naoprotestado',
    'sustado',
    'devolvido',
    'cancelado',
    'erroapresentartitulo',
    'retirado',
  ].includes(protest);
  const mode: 'download' | 'generate' | 'disabled' =
    row.statusValue === 'Registering'
      ? 'disabled'
      : exists
        ? registration === 'baixado' || settlement === 'settlement' || row.statusValue === 'Paid'
          ? 'disabled'
          : 'download'
        : row.registered !== true &&
            ['NotRegistered', 'OverDue', 'PartialPaid', 'ReturnedFromDebtCollection'].includes(
              row.statusValue,
            )
          ? 'generate'
          : 'disabled';
  return {
    mode,
    canCancel: registered,
    canChangeDueDate: registered && protestInactive,
    canSendEmail: exists,
    overdue: row.statusValue === 'OverDue',
  };
}
